import { useMemo } from 'react';

import { useCalendlyCTAVisibility } from 'hooks/use-calendly-cta-visibility';
import { useLeadFormVisibility } from 'hooks/use-lead-form-visibility';
import { ToolConfig, ToolKind, ToolTrigger } from 'shared/interfaces/tools/Tool';

export type ToolVisibilityOverrides = Partial<Record<ToolKind, boolean>>;

interface UseToolVisibilityProps {
  toolConfigs: ToolConfig[];
  smartAssistantId: string;
  conversationLength: number;
  visibilityOverrides?: ToolVisibilityOverrides;
}

export function useToolVisibility({
  toolConfigs,
  smartAssistantId,
  conversationLength,
  visibilityOverrides = {}
}: UseToolVisibilityProps) {
  const [showLeadForm] = useLeadFormVisibility(smartAssistantId);
  const [showCalendlyCTA] = useCalendlyCTAVisibility(smartAssistantId);

  const visibleToolKinds = useMemo(() => {
    const visibleKinds: ToolKind[] = [];

    toolConfigs.forEach((toolConfig) => {
      if (!toolConfig.enabled) return;
      if (visibleKinds.includes(toolConfig.kind as ToolKind)) return;

      // Check override first
      const override = visibilityOverrides[toolConfig.kind as ToolKind];
      if (override !== undefined) {
        if (override) {
          visibleKinds.push(toolConfig.kind);
        }
        return;
      }

      const triggerMatches =
        toolConfig.trigger === ToolTrigger.WELCOME ||
        (conversationLength > 1 && toolConfig.trigger === ToolTrigger.FIRST_SMART_ASSISTANT_MESSAGE);

      // Default logic for each tool type
      let shouldShow = false;
      switch (toolConfig.kind) {
        case ToolKind.COLLECT_LEAD_INFO:
          shouldShow = showLeadForm && triggerMatches;
          break;
        case ToolKind.CALENDLY:
          shouldShow = showCalendlyCTA && triggerMatches;
          break;
        default:
          // For non-static tools we default to showing them since they rely on events in the conversation
          shouldShow = true;
      }

      if (shouldShow) visibleKinds.push(toolConfig.kind);
    });

    return visibleKinds;
  }, [toolConfigs, visibilityOverrides, showLeadForm, showCalendlyCTA, conversationLength]);

  return { visibleToolKinds };
}
